import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  TableSortLabel,
  Chip,
  Button,
  Collapse,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useApiClient } from '../utils/apiClient';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_CODE_COLORS = {
  '2xx': { bg: 'rgba(76, 175, 80, 0.7)', border: 'rgba(76, 175, 80, 1)' },
  '3xx': { bg: 'rgba(255, 193, 7, 0.7)', border: 'rgba(255, 193, 7, 1)' },
  '4xx': { bg: 'rgba(255, 152, 0, 0.7)', border: 'rgba(255, 152, 0, 1)' },
  '5xx': { bg: 'rgba(244, 67, 54, 0.7)', border: 'rgba(244, 67, 54, 1)' },
  other: { bg: 'rgba(158, 158, 158, 0.7)', border: 'rgba(158, 158, 158, 1)' },
};

function getStatusClass(code) {
  const n = Number(code);
  if (n >= 200 && n < 300) return '2xx';
  if (n >= 300 && n < 400) return '3xx';
  if (n >= 400 && n < 500) return '4xx';
  if (n >= 500 && n < 600) return '5xx';
  return 'other';
}

function getStatusChipColor(code) {
  const cls = getStatusClass(code);
  if (cls === '5xx') return 'error';
  if (cls === '4xx') return 'warning';
  if (cls === '2xx') return 'success';
  return 'default';
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatDateFull(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function descendingComparator(a, b, orderBy) {
  const va = a[orderBy];
  const vb = b[orderBy];
  if (vb < va) return -1;
  if (vb > va) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function TrendIndicator({ current, previous }) {
  if (previous === 0 && current === 0) return <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  if (previous === 0) return <TrendingUpIcon fontSize="small" color="error" />;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (Math.abs(pct) < 5) return <TrendingFlatIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  if (pct > 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TrendingUpIcon fontSize="small" color="error" />
        <Typography variant="caption" color="error">+{pct}%</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TrendingDownIcon fontSize="small" color="success" />
      <Typography variant="caption" color="success.main">{pct}%</Typography>
    </Box>
  );
}

// ─── Column resize ───────────────────────────────────────────────────────────

/**
 * Returns per-column widths (px) and a mousedown handler that starts a drag.
 * Columns cannot shrink below 60px. Attach the handler to a drag-handle div
 * placed at the right edge of each header cell.
 */
function useColumnResize(initialWidths) {
  const [widths, setWidths] = useState(initialWidths);
  const dragging = useRef(null);
  // Keep a ref to current widths so handleMouseDown never goes stale
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const handleMouseDown = useCallback((col, e) => {
    e.preventDefault();
    dragging.current = { col, startX: e.clientX, startWidth: widthsRef.current[col] };

    const onMove = (mv) => {
      if (!dragging.current) return;
      // Destructure before setWidths — the updater runs async and dragging.current
      // may be null by the time React calls it.
      const { col: dragCol, startX, startWidth } = dragging.current;
      const next = Math.max(60, startWidth + (mv.clientX - startX));
      setWidths(prev => ({ ...prev, [dragCol]: next }));
    };

    const onUp = () => {
      dragging.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []); // stable — reads widths via widthsRef, not the closure

  return { widths, handleMouseDown };
}

function ResizableHeaderCell({ col, widths, onMouseDown, align, sx, children, ...rest }) {
  return (
    <TableCell
      align={align}
      sx={{
        position: 'relative',
        width: widths[col],
        minWidth: widths[col],
        maxWidth: widths[col],
        userSelect: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...sx,
      }}
      {...rest}
    >
      {children}
      {/* drag handle — always-visible divider line with a wider invisible grab zone */}
      <Box
        onMouseDown={(e) => onMouseDown(col, e)}
        sx={{
          position: 'absolute',
          right: 0,
          top: '15%',
          bottom: '15%',
          width: 12,
          cursor: 'col-resize',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Always-visible 2px divider in the center of the grab zone
          '&::after': {
            content: '""',
            display: 'block',
            width: 2,
            height: '100%',
            bgcolor: 'text.secondary',
            opacity: 0.4,
            borderRadius: 1,
            transition: 'background-color 0.15s, width 0.15s, opacity 0.15s',
          },
          '&:hover::after': {
            bgcolor: 'primary.main',
            opacity: 1,
            width: 3,
          },
          '&:active::after': {
            bgcolor: 'primary.dark',
            opacity: 1,
            width: 3,
          },
        }}
      />
    </TableCell>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color = 'primary.main' }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold" color={color} sx={{ my: 0.5 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function ExpandableExamples({ examples }) {
  const [open, setOpen] = useState(false);
  if (!examples || examples.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            maxWidth: 360,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
          }}
        >
          {examples[0]}
        </Typography>
        {examples.length > 1 && (
          <IconButton size="small" onClick={() => setOpen(v => !v)} sx={{ p: 0 }}>
            {open ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
          </IconButton>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
          {examples.slice(1).map((ex, i) => (
            <Typography
              key={i}
              variant="caption"
              display="block"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 0.5 }}
            >
              {ex}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const DailyErrorReport = () => {
  const apiClient = useApiClient();
  const printRef = useRef(null);
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const [dateRange, setDateRange] = useState('previous-day');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Trend table sort
  const [trendOrder, setTrendOrder] = useState('desc');
  const [trendOrderBy, setTrendOrderBy] = useState('totalCount');

  // Detail records (lazy-loaded raw shipment errors)
  const [rawRecords, setRawRecords] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState(null);

  // Detail table sort
  const [detailOrder, setDetailOrder] = useState('desc');
  const [detailOrderBy, setDetailOrderBy] = useState('date');

  // Resizable column widths
  const trendCols = useColumnResize({
    status: 90,
    endpoint: 220,
    errorGroup: 420,
    total: 80,
    last7d: 80,
    trend: 140,
  });

  const detailCols = useColumnResize({
    date: 110,
    status: 90,
    endpoint: 200,
    timestamp: 190,
    errorMessage: 520,
  });

  // ── Date helpers ────────────────────────────────────────────────────────

  const getDateRange = useCallback(() => {
    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    let startDate, endDate;

    switch (dateRange) {
      case 'previous-day': {
        // Yesterday midnight→midnight in ET. We derive the ET calendar date via
        // Intl.DateTimeFormat (YYYY-MM-DD, en-CA locale) then construct local
        // Date objects at 00:00:00 / 23:59:59 for that day.
        const etFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });
        const yesterdayET = etFormatter.format(new Date(Date.now() - 24 * 60 * 60 * 1000));
        const [vy, vm, vd] = yesterdayET.split('-').map(Number);
        startDate = new Date(vy, vm - 1, vd, 0, 0, 0, 0);
        endDate   = new Date(vy, vm - 1, vd, 23, 59, 59, 999);
        break;
      }
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          // Parse the YYYY-MM-DD strings as local dates and enforce full-day bounds:
          // start = 00:00:00.000, end = 23:59:59.999
          const [sy, sm, sd] = customStartDate.split('-').map(Number);
          const [ey, em, ed] = customEndDate.split('-').map(Number);
          startDate = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
          endDate = new Date(ey, em - 1, ed, 23, 59, 59, 999);
        } else {
          return null;
        }
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
    }

    return { startDate, endDate };
  }, [dateRange, customStartDate, customEndDate]);

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchReport = useCallback(async () => {
    const range = getDateRange();
    if (!range) {
      setError('Please select both start and end dates for custom range');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/analytics/reports/daily-errors', {
        params: {
          startDate: range.startDate.toISOString(),
          endDate: range.endDate.toISOString(),
        },
      });
      setReportData(response.data);
    } catch (err) {
      console.error('Error fetching daily error report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReport();
    fetchDetails();
  }, [dateRange, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetails = useCallback(async () => {
    const range = getDateRange();
    if (!range) return;

    setRawLoading(true);
    setRawError(null);

    try {
      const response = await apiClient.get('/analytics/reports/daily-errors/raw', {
        params: {
          startDate: range.startDate.toISOString(),
          endDate: range.endDate.toISOString(),
        },
      });
      setRawRecords(response.data);
    } catch (err) {
      console.error('Error fetching detail records:', err);
      setRawError(err.response?.data?.message || err.message || 'Failed to fetch detail records');
    } finally {
      setRawLoading(false);
    }
  }, [getDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived data ─────────────────────────────────────────────────────────

  const totalErrors = reportData
    ? reportData.dailySummary.reduce((s, d) => s + d.totalErrors, 0)
    : 0;

  const busiestDay = reportData && reportData.dailySummary.length > 0
    ? reportData.dailySummary.reduce((a, b) => (b.totalErrors > a.totalErrors ? b : a))
    : null;

  const topGroup = reportData && reportData.errorGroups.length > 0
    ? reportData.errorGroups[0]
    : null;

  // Collect all unique status code classes present in data
  const statusClasses = reportData
    ? [...new Set(
        reportData.dailySummary.flatMap(d => Object.keys(d.byStatusCode).map(getStatusClass)),
      )].sort()
    : [];

  // ── Chart data ───────────────────────────────────────────────────────────

  const chartData = reportData
    ? {
        labels: reportData.dates.map(formatDate),
        datasets: statusClasses.map(cls => ({
          label: cls,
          data: reportData.dates.map(date => {
            const day = reportData.dailySummary.find(d => d.date === date);
            if (!day) return 0;
            return Object.entries(day.byStatusCode)
              .filter(([code]) => getStatusClass(code) === cls)
              .reduce((s, [, v]) => s + v, 0);
          }),
          backgroundColor: (STATUS_CODE_COLORS[cls] || STATUS_CODE_COLORS.other).bg,
          borderColor: (STATUS_CODE_COLORS[cls] || STATUS_CODE_COLORS.other).border,
          borderWidth: 1,
        })),
      }
    : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
      tooltip: {
        callbacks: {
          footer: (items) => {
            const total = items.reduce((s, i) => s + i.raw, 0);
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  // ── Trend table rows ──────────────────────────────────────────────────────

  const trendRows = reportData
    ? reportData.errorGroups.map(g => {
        // Compare last 7 days vs prior 7 days
        const dates = reportData.dates;
        const last7 = dates.slice(-7);
        const prior7 = dates.slice(-14, -7);
        const last7Count = last7.reduce((s, d) => s + (g.byDate[d] || 0), 0);
        const prior7Count = prior7.reduce((s, d) => s + (g.byDate[d] || 0), 0);
        return { ...g, last7Count, prior7Count };
      })
    : [];

  const sortedTrendRows = [...trendRows].sort(getComparator(trendOrder, trendOrderBy));

  // ── Detail records sort ───────────────────────────────────────────────────

  const sortedDetailRows = rawRecords
    ? [...rawRecords].sort(getComparator(detailOrder, detailOrderBy))
    : [];

  // ── CSV download ──────────────────────────────────────────────────────────

  const [csvLoading, setCsvLoading] = useState(false);

  const handleDownloadCSV = async () => {
    const range = getDateRange();
    if (!range) return;

    // Reuse already-loaded detail records to avoid a redundant network request.
    let records = rawRecords;

    if (!records) {
      setCsvLoading(true);
      try {
        const response = await apiClient.get('/analytics/reports/daily-errors/raw', {
          params: {
            startDate: range.startDate.toISOString(),
            endDate: range.endDate.toISOString(),
          },
        });
        records = response.data;
        setRawRecords(records);
      } catch (err) {
        console.error('Error downloading CSV:', err);
        setCsvLoading(false);
        return;
      }
      setCsvLoading(false);
    }

    const jsonCell = (val) => {
      const str = val == null ? '' : (typeof val === 'string' ? val : JSON.stringify(val));
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = [
      'date', 'timestampUTC', 'statusCode', 'endpoint', 'errorMessage',
      'sourceRequestBody', 'shipiumRequestBody', 'shipiumResponseBody', 'sourceResponseBody',
    ];
    const rows = records.map(r => [
      r.date,
      r.timestampUtc,
      r.statusCode,
      `"${(r.endpoint || '').replace(/"/g, '""')}"`,
      `"${(r.errorMessage || '').replace(/"/g, '""')}"`,
      jsonCell(r.sourceRequestBody),
      jsonCell(r.shipiumRequestBody),
      jsonCell(r.shipiumResponseBody),
      jsonCell(r.sourceResponseBody),
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const rangeLabel = `${range.startDate.toISOString().split('T')[0]}_${range.endDate.toISOString().split('T')[0]}`;
    a.download = `error-report-raw-${rangeLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print ─────────────────────────────────────────────────────────────────

  const handlePrint = () => {
    window.print();
  };

  // ── Sort handlers ─────────────────────────────────────────────────────────

  const handleTrendSort = (col) => {
    const isAsc = trendOrderBy === col && trendOrder === 'asc';
    setTrendOrder(isAsc ? 'desc' : 'asc');
    setTrendOrderBy(col);
  };

  const handleDetailSort = (col) => {
    const isAsc = detailOrderBy === col && detailOrder === 'asc';
    setDetailOrder(isAsc ? 'desc' : 'asc');
    setDetailOrderBy(col);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #error-report-printable, #error-report-printable * { visibility: visible; }
          #error-report-printable { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }

          /* Use landscape + tight margins to maximise usable width */
          @page { size: landscape; margin: 1cm; }

          /* Base font reduction — everything scales from here */
          #error-report-printable { font-size: 8px !important; }

          /* MUI typography overrides */
          #error-report-printable .MuiTypography-root { font-size: inherit !important; line-height: 1.3 !important; }
          #error-report-printable .MuiTypography-h6 { font-size: 10px !important; }
          #error-report-printable .MuiTypography-subtitle1 { font-size: 9px !important; }
          #error-report-printable .MuiTypography-body1 { font-size: 8px !important; }
          #error-report-printable .MuiTypography-body2 { font-size: 8px !important; }
          #error-report-printable .MuiTypography-caption { font-size: 7px !important; }
          #error-report-printable .MuiTypography-h4 { font-size: 14px !important; }

          /* Table cells — tighter padding and smaller font */
          #error-report-printable .MuiTableCell-root {
            font-size: 7px !important;
            padding: 2px 4px !important;
            line-height: 1.2 !important;
          }
          #error-report-printable .MuiTableCell-head {
            font-size: 7px !important;
            font-weight: bold !important;
          }

          /* Chips (status codes) */
          #error-report-printable .MuiChip-root {
            font-size: 6px !important;
            height: 14px !important;
          }
          #error-report-printable .MuiChip-label { padding: 0 4px !important; }

          /* Summary cards — remove excess padding */
          #error-report-printable .MuiCardContent-root { padding: 6px 8px !important; }
          #error-report-printable .MuiCardHeader-root { padding: 4px 8px !important; }
          #error-report-printable .MuiCard-root {
            box-shadow: none !important;
            border: 1px solid #ddd !important;
            margin-bottom: 6px !important;
          }

          /* Tables — allow content to break across pages, no overflow clipping */
          #error-report-printable .MuiTableContainer-root {
            overflow: visible !important;
            max-width: 100% !important;
          }
          #error-report-printable table {
            width: 100% !important;
            table-layout: auto !important;
          }

          /* Word-wrap long error messages so nothing gets cut off */
          #error-report-printable td { word-break: break-word !important; white-space: normal !important; }
        }
      `}</style>

      <Box id="error-report-printable" ref={printRef}>

        {/* ── Controls ── */}
        <Box className="no-print" sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Date Range</InputLabel>
            <Select value={dateRange} onChange={e => setDateRange(e.target.value)} label="Date Range">
              <MenuItem value="previous-day">Previous Day</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {dateRange === 'custom' && (
            <>
              <TextField
                label="From (00:00:00)"
                type="date"
                size="small"
                value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { colorScheme: theme.palette.mode } }}
                helperText="Start of day · 00:00:00"
              />
              <TextField
                label="To (23:59:59)"
                type="date"
                size="small"
                value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { colorScheme: theme.palette.mode } }}
                helperText="End of day · 23:59:59"
              />
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            size="small"
            startIcon={csvLoading ? <CircularProgress size={14} /> : <DownloadIcon />}
            onClick={handleDownloadCSV}
            disabled={!reportData || csvLoading}
          >
            {csvLoading ? 'Downloading…' : 'Download CSV'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={!reportData}
          >
            Print / Save PDF
          </Button>
        </Box>

        {/* ── Loading / Error ── */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && reportData && (
          <>
            {/* ══ SECTION A — 30-Day Trend Summary ══ */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Trend Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {dateRange === 'previous-day' ? 'Previous day'
                  : dateRange === 'custom' && customStartDate && customEndDate
                  ? `${formatDateFull(customStartDate)} – ${formatDateFull(customEndDate)}`
                  : dateRange === '7d' ? 'Last 7 days'
                  : dateRange === '90d' ? 'Last 90 days'
                  : 'Last 30 days'}{' '}
                · All times Eastern (America/New_York)
              </Typography>
            </Box>

            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Total Errors"
                  value={totalErrors.toLocaleString()}
                  sub={`across ${reportData.dates.length} days`}
                  color={totalErrors === 0 ? 'success.main' : 'error.main'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Unique Error Groups"
                  value={reportData.errorGroups.length.toLocaleString()}
                  sub="distinct status + endpoint + message combinations"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Busiest Error Day"
                  value={busiestDay ? busiestDay.totalErrors.toLocaleString() : '—'}
                  sub={busiestDay ? formatDateFull(busiestDay.date) : 'No data'}
                  color="warning.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard
                  label="Top Error Group"
                  value={topGroup ? topGroup.totalCount.toLocaleString() : '—'}
                  sub={topGroup ? `${topGroup.statusCode} · ${topGroup.endpoint}` : 'No errors'}
                  color="error.main"
                />
              </Grid>
            </Grid>

            {/* Stacked bar chart */}
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title="Daily Error Volume by Status Code"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
              />
              <CardContent>
                {reportData.dates.length === 0 ? (
                  <Typography color="text.secondary">No errors in this period.</Typography>
                ) : (
                  <Box sx={{ height: 280 }}>
                    <Bar data={chartData} options={chartOptions} />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Error groups trend table */}
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title="Error Groups — Trend Table"
                subheader="Grouped by status code, endpoint, and normalized error message. ↑↓ compares last 7 days vs prior 7 days."
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent sx={{ p: 0 }}>
                {sortedTrendRows.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Typography color="text.secondary">No error groups found.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table
                      size="small"
                      sx={{
                        tableLayout: 'fixed',
                        width: Object.values(trendCols.widths).reduce((a, b) => a + b, 0),
                        minWidth: '100%',
                      }}
                    >
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <ResizableHeaderCell col="status" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown}>
                            <TableSortLabel
                              active={trendOrderBy === 'statusCode'}
                              direction={trendOrderBy === 'statusCode' ? trendOrder : 'asc'}
                              onClick={() => handleTrendSort('statusCode')}
                            >
                              Status
                            </TableSortLabel>
                          </ResizableHeaderCell>
                          <ResizableHeaderCell col="endpoint" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown}>
                            Endpoint
                          </ResizableHeaderCell>
                          <ResizableHeaderCell col="errorGroup" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown}>
                            Error Group
                          </ResizableHeaderCell>
                          <ResizableHeaderCell col="total" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown} align="right">
                            <TableSortLabel
                              active={trendOrderBy === 'totalCount'}
                              direction={trendOrderBy === 'totalCount' ? trendOrder : 'asc'}
                              onClick={() => handleTrendSort('totalCount')}
                            >
                              Total
                            </TableSortLabel>
                          </ResizableHeaderCell>
                          <ResizableHeaderCell col="last7d" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown} align="right">
                            <TableSortLabel
                              active={trendOrderBy === 'last7Count'}
                              direction={trendOrderBy === 'last7Count' ? trendOrder : 'asc'}
                              onClick={() => handleTrendSort('last7Count')}
                            >
                              Last 7d
                            </TableSortLabel>
                          </ResizableHeaderCell>
                          <ResizableHeaderCell col="trend" widths={trendCols.widths} onMouseDown={trendCols.handleMouseDown}>
                            Trend (7d vs prior)
                          </ResizableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedTrendRows.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ width: trendCols.widths.status, overflow: 'hidden' }}>
                              <Chip
                                label={row.statusCode}
                                size="small"
                                color={getStatusChipColor(row.statusCode)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ width: trendCols.widths.endpoint, overflow: 'hidden' }}>
                              <Tooltip title={row.endpoint} placement="top">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: 'monospace',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }}
                                >
                                  {row.endpoint}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ width: trendCols.widths.errorGroup }}>
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                              >
                                {row.errorGroupKey}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ width: trendCols.widths.total }}>
                              <Typography variant="body2" fontWeight="bold">
                                {row.totalCount.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ width: trendCols.widths.last7d }}>
                              <Typography variant="body2">
                                {row.last7Count.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: trendCols.widths.trend }}>
                              <TrendIndicator current={row.last7Count} previous={row.prior7Count} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* ══ SECTION B — Error Detail Records ══ */}
            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListAltIcon color="action" />
                  <Typography variant="h6" fontWeight="bold">
                    Error Detail Records
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Individual shipment-level error records with full error messages for the selected date range.
                </Typography>
              </Box>
              {rawRecords && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {rawRecords.length.toLocaleString()} records loaded
                </Typography>
              )}
            </Box>

            {rawLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {rawError && (
              <Alert severity="error" sx={{ mb: 2 }}>{rawError}</Alert>
            )}

            {rawRecords && !rawLoading && (
              <Card>
                <CardContent sx={{ p: 0 }}>
                  {sortedDetailRows.length === 0 ? (
                    <Box sx={{ p: 3 }}>
                      <Alert severity="success">No error records found for this date range.</Alert>
                    </Box>
                  ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: 'fixed',
                          width: Object.values(detailCols.widths).reduce((a, b) => a + b, 0),
                          minWidth: '100%',
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <ResizableHeaderCell col="date" widths={detailCols.widths} onMouseDown={detailCols.handleMouseDown}>
                              <TableSortLabel
                                active={detailOrderBy === 'date'}
                                direction={detailOrderBy === 'date' ? detailOrder : 'asc'}
                                onClick={() => handleDetailSort('date')}
                              >
                                Date (ET)
                              </TableSortLabel>
                            </ResizableHeaderCell>
                            <ResizableHeaderCell col="status" widths={detailCols.widths} onMouseDown={detailCols.handleMouseDown}>
                              <TableSortLabel
                                active={detailOrderBy === 'statusCode'}
                                direction={detailOrderBy === 'statusCode' ? detailOrder : 'asc'}
                                onClick={() => handleDetailSort('statusCode')}
                              >
                                Status
                              </TableSortLabel>
                            </ResizableHeaderCell>
                            <ResizableHeaderCell col="endpoint" widths={detailCols.widths} onMouseDown={detailCols.handleMouseDown}>
                              <TableSortLabel
                                active={detailOrderBy === 'endpoint'}
                                direction={detailOrderBy === 'endpoint' ? detailOrder : 'asc'}
                                onClick={() => handleDetailSort('endpoint')}
                              >
                                Endpoint
                              </TableSortLabel>
                            </ResizableHeaderCell>
                            <ResizableHeaderCell col="timestamp" widths={detailCols.widths} onMouseDown={detailCols.handleMouseDown}>
                              Timestamp (UTC)
                            </ResizableHeaderCell>
                            <ResizableHeaderCell col="errorMessage" widths={detailCols.widths} onMouseDown={detailCols.handleMouseDown}>
                              Error Message
                            </ResizableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedDetailRows.map((row, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ width: detailCols.widths.date }}>
                                <Typography variant="caption">{row.date}</Typography>
                              </TableCell>
                              <TableCell sx={{ width: detailCols.widths.status, overflow: 'hidden' }}>
                                <Chip
                                  label={row.statusCode}
                                  size="small"
                                  color={getStatusChipColor(row.statusCode)}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell sx={{ width: detailCols.widths.endpoint, overflow: 'hidden' }}>
                                <Tooltip title={row.endpoint} placement="top">
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: 'monospace',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'block',
                                    }}
                                  >
                                    {row.endpoint}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ width: detailCols.widths.timestamp, overflow: 'hidden' }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                                >
                                  {row.timestampUtc}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: detailCols.widths.errorMessage }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                                >
                                  {row.errorMessage}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}

          </>
        )}

        {!loading && !error && !reportData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography color="text.secondary">No data loaded.</Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default DailyErrorReport;
